import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from './AuthContext';

export interface UserPreferences {
  font_size: 'small' | 'medium' | 'large' | 'xlarge';
  font_family: 'default' | 'dyslexic' | 'monospace';
  high_contrast: boolean;
  reduced_motion: boolean;
  lesson_break_reminders: boolean;
  text_to_speech: boolean;
  playback_speed: number;
  notification_frequency: 'off' | 'minimal' | 'normal' | 'frequent';
  connection_mode: 'solo' | 'silent_cohort' | 'buddy';
  streak_reminder_enabled: boolean;
}

const defaultPreferences: UserPreferences = {
  font_size: 'medium',
  font_family: 'default',
  high_contrast: false,
  reduced_motion: false,
  lesson_break_reminders: true,
  text_to_speech: false,
  playback_speed: 1.0,
  notification_frequency: 'normal',
  connection_mode: 'solo',
  streak_reminder_enabled: true
};

interface PreferencesContextType {
  preferences: UserPreferences;
  loading: boolean;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  resetPreferences: () => Promise<void>;
}

const PreferencesContext = createContext<PreferencesContextType>({
  preferences: defaultPreferences,
  loading: true,
  updatePreferences: async () => {},
  resetPreferences: async () => {}
});

export function usePreferences() {
  return useContext(PreferencesContext);
}

interface PreferencesProviderProps {
  children: ReactNode;
}

export function PreferencesProvider({ children }: PreferencesProviderProps) {
  const { user } = useAuth();
  const { fetchApi } = useApi();
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [loading, setLoading] = useState(true);

  // Load preferences when user changes
  useEffect(() => {
    if (user) {
      loadPreferences();
    } else {
      // Load from localStorage for non-logged-in users
      const stored = localStorage.getItem('preferences');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setPreferences({ ...defaultPreferences, ...parsed });
        } catch {
          setPreferences(defaultPreferences);
        }
      }
      setLoading(false);
    }
  }, [user]);

  // Apply preferences to document
  useEffect(() => {
    applyPreferences(preferences);
  }, [preferences]);

  async function loadPreferences() {
    try {
      // API returns numeric booleans from SQLite
      interface ApiPreferences {
        font_size: 'small' | 'medium' | 'large' | 'xlarge';
        font_family: 'default' | 'dyslexic' | 'monospace';
        high_contrast: number;
        reduced_motion: number;
        lesson_break_reminders: number;
        text_to_speech: number;
        playback_speed: number;
        notification_frequency: 'off' | 'minimal' | 'normal' | 'frequent';
        connection_mode: 'solo' | 'silent_cohort' | 'buddy';
        streak_reminder_enabled: number;
      }

      const data = await fetchApi<{ preferences: ApiPreferences }>('/api/preferences');
      // Convert numeric booleans from SQLite
      const prefs: UserPreferences = {
        font_size: data.preferences.font_size,
        font_family: data.preferences.font_family,
        high_contrast: Boolean(data.preferences.high_contrast),
        reduced_motion: Boolean(data.preferences.reduced_motion),
        lesson_break_reminders: Boolean(data.preferences.lesson_break_reminders),
        text_to_speech: Boolean(data.preferences.text_to_speech),
        playback_speed: data.preferences.playback_speed,
        notification_frequency: data.preferences.notification_frequency,
        connection_mode: data.preferences.connection_mode || 'solo',
        streak_reminder_enabled: data.preferences.streak_reminder_enabled !== 0
      };
      setPreferences(prefs);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  }

  const updatePreferences = useCallback(async (updates: Partial<UserPreferences>) => {
    setPreferences(prev => {
      const newPrefs = { ...prev, ...updates };

      if (user) {
        // Fire and forget the API call
        fetchApi('/api/preferences', {
          method: 'PUT',
          body: JSON.stringify(updates)
        }).catch(error => {
          console.error('Failed to save preferences:', error);
          // Revert on error
          setPreferences(prev);
        });
      } else {
        // Save to localStorage for non-logged-in users
        localStorage.setItem('preferences', JSON.stringify(newPrefs));
      }

      return newPrefs;
    });
  }, [user, fetchApi]);

  const resetPreferences = useCallback(async () => {
    if (user) {
      try {
        await fetchApi('/api/preferences/reset', { method: 'POST' });
        setPreferences(defaultPreferences);
      } catch (error) {
        console.error('Failed to reset preferences:', error);
        throw error;
      }
    } else {
      localStorage.removeItem('preferences');
      setPreferences(defaultPreferences);
    }
  }, [user, fetchApi]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ preferences, loading, updatePreferences, resetPreferences }),
    [preferences, loading, updatePreferences, resetPreferences]
  );

  return (
    <PreferencesContext.Provider value={contextValue}>
      {children}
    </PreferencesContext.Provider>
  );
}

// Apply preferences as CSS custom properties
function applyPreferences(prefs: UserPreferences) {
  const root = document.documentElement;

  // Font size
  const fontSizes = {
    small: '14px',
    medium: '16px',
    large: '18px',
    xlarge: '20px'
  };
  root.style.setProperty('--base-font-size', fontSizes[prefs.font_size]);

  // Font family
  const fontFamilies = {
    default: 'Inter, system-ui, sans-serif',
    dyslexic: 'OpenDyslexic, Comic Sans MS, sans-serif',
    monospace: 'JetBrains Mono, Consolas, monospace'
  };
  root.style.setProperty('--font-family', fontFamilies[prefs.font_family]);

  // High contrast
  if (prefs.high_contrast) {
    root.classList.add('high-contrast');
  } else {
    root.classList.remove('high-contrast');
  }

  // Reduced motion
  if (prefs.reduced_motion) {
    root.classList.add('reduced-motion');
  } else {
    root.classList.remove('reduced-motion');
  }
}
